import { Suspense, lazy } from "react";
import { Menu as MenuIcon } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Sidebar } from "./components/Sidebar";
import { getAdminProfile } from "./api/auth";
import { LoginPage } from "./pages/LoginPage";
import { TenantLoginPage } from "./pages/TenantLoginPage";
import { TenantPortalPage } from "./pages/TenantPortalPage";
import { getPendingDocumentCount } from "./api/tenant-documents";
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const PropertiesPage = lazy(() =>
  import("./pages/PropertiesPage").then((m) => ({ default: m.PropertiesPage })),
);
const TenantsPage = lazy(() =>
  import("./pages/TenantsPage").then((m) => ({ default: m.TenantsPage })),
);
const RoomsPage = lazy(() =>
  import("./pages/RoomsPage").then((m) => ({ default: m.RoomsPage })),
);
const InspectionsPage = lazy(() =>
  import("./pages/InspectionsPage").then((m) => ({
    default: m.InspectionsPage,
  })),
);
const TenanciesPage = lazy(() =>
  import("./pages/TenanciesPage").then((m) => ({ default: m.TenanciesPage })),
);
const MaintenancePage = lazy(() =>
  import("./pages/MaintenancePage").then((m) => ({
    default: m.MaintenancePage,
  })),
);
const DocumentReviewList = lazy(() =>
  import("./components/DocumentReviewList").then((m) => ({
    default: m.DocumentReviewList,
  })),
);
const FilesPage = lazy(() =>
  import("./pages/FilesPage").then((m) => ({ default: m.FilesPage })),
);
const TenantUsersPage = lazy(() =>
  import("./pages/TenantUsersPage").then((m) => ({
    default: m.TenantUsersPage,
  })),
);
const BackupRestorePage = lazy(() =>
  import("./pages/BackupRestorePage").then((m) => ({
    default: m.BackupRestorePage,
  })),
);
const AdminUsersPage = lazy(() =>
  import("./pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })),
);

type AuthUser = {
  id?: number;
  name: string;
  email: string;
  token: string;
  role?: "admin" | "manager" | "inspector" | "file_viewer" | "tenant";
};

const navItems = [
  { key: "dashboard", label: "Dashboard" },
  { key: "properties", label: "Properties" },
  { key: "tenants", label: "Tenants" },
  { key: "tenant-users", label: "Tenant Users" },
  { key: "rooms", label: "Rooms" },
  { key: "tenancies", label: "Tenancies" },
  { key: "maintenance", label: "Maintenance" },
  { key: "inspections", label: "Inspections" },
  { key: "document-reviews", label: "Document Reviews" },
  { key: "files", label: "Files" },
  { key: "backup-restore", label: "Backup & Restore" },
  { key: "admin-users", label: "User Accounts" },
];

const drawerWidth = 280;

const VALID_VIEW_KEYS = navItems.map((i) => i.key);

function getInitialView() {
  const hash = window.location.hash.replace(/^#\/?/, "");
  return VALID_VIEW_KEYS.includes(hash) ? hash : "dashboard";
}

function App() {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [activeView, setActiveView] = useState(getInitialView);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginMode, setLoginMode] = useState<"admin" | "tenant">("tenant");
  const [pendingDocumentCount, setPendingDocumentCount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const savedSession = window.localStorage.getItem("hmo-manager-auth");
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession) as AuthUser;
        if (parsed.role) {
          setAuthUser(parsed);
          if (parsed.role === "file_viewer") setActiveView("files");
        } else {
          void getAdminProfile()
            .then((profile) => {
              const hydrated = {
                ...parsed,
                id: profile.id,
                name: profile.full_name,
                email: profile.email,
                role: profile.role,
              } satisfies AuthUser;
              setAuthUser(hydrated);
              window.localStorage.setItem(
                "hmo-manager-auth",
                JSON.stringify(hydrated),
              );
            })
            .catch(() => {
              window.localStorage.removeItem("hmo-manager-auth");
            });
        }
      } catch {
        window.localStorage.removeItem("hmo-manager-auth");
      }
    }
  }, []);

  useEffect(() => {
    if (
      !authUser ||
      authUser.role === "tenant" ||
      authUser.role === "file_viewer"
    ) {
      setPendingDocumentCount(0);
      return;
    }

    let cancelled = false;
    const loadPendingCount = async () => {
      try {
        const result = await getPendingDocumentCount(authUser.token);
        if (!cancelled) {
          setPendingDocumentCount(result.count);
        }
      } catch {
        // The badge is supplementary; keep the existing value on transient failures.
      }
    };

    void loadPendingCount();
    const timer = window.setInterval(loadPendingCount, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [authUser, pendingDocumentCount]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace(/^#\/?/, "");
      if (VALID_VIEW_KEYS.includes(hash)) {
        if (authUser?.role === "file_viewer") {
          setActiveView("files");
        } else if (hash === "admin-users" && authUser?.role !== "admin") {
          setActiveView("dashboard");
          window.location.hash = "#/dashboard";
        } else {
          setActiveView(hash);
        }
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [authUser]);

  const handleLogin = (user: AuthUser) => {
    setAuthUser(user);
    window.localStorage.setItem("hmo-manager-auth", JSON.stringify(user));
    const hash = window.location.hash.replace(/^#\/?/, "");
    if (user.role === "file_viewer") {
      window.location.hash = "#/files";
    } else if (!VALID_VIEW_KEYS.includes(hash)) {
      window.location.hash = "#/dashboard";
    }
  };

  const handleTenantLogin = (user: Omit<AuthUser, "token">, token: string) => {
    const authData = { ...user, token, role: "tenant" as const } as AuthUser;
    setAuthUser(authData);
    window.localStorage.setItem("hmo-manager-auth", JSON.stringify(authData));
  };

  const handleLogout = () => {
    setAuthUser(null);
    window.localStorage.removeItem("hmo-manager-auth");
    window.location.hash = "";
  };

  const handleDrawerToggle = () => {
    setMobileOpen((current) => !current);
  };

  const isTenant = authUser?.role === "tenant";
  const isFileViewer = authUser?.role === "file_viewer";
  const isAdmin = authUser?.role === "admin";
  const visibleNavItems = isFileViewer
    ? navItems.filter((item) => item.key === "files")
    : navItems.filter((item) => item.key !== "admin-users" || isAdmin);
  const tenantToken = isTenant ? (authUser?.token ?? "") : "";

  const navigateTo = (view: string) => {
    if (isFileViewer && view !== "files") return;
    if (view === "admin-users" && !isAdmin) return;
    setActiveView(view);
    window.location.hash = `#/${view}`;
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
      }}
    >
      <Sidebar
        items={visibleNavItems}
        activeView={activeView}
        onNavigate={navigateTo}
        onLogout={handleLogout}
        userName={authUser?.name ?? ""}
        pendingDocumentCount={pendingDocumentCount}
      />
    </Box>
  );

  if (!authUser) {
    if (loginMode === "tenant") {
      return (
        <TenantLoginPage
          onLogin={(user, token) => handleTenantLogin(user, token)}
          onSwitchToAdmin={() => setLoginMode("admin")}
        />
      );
    }
    return (
      <LoginPage
        onLogin={handleLogin}
        onSwitchToTenant={() => setLoginMode("tenant")}
      />
    );
  }

  if (isTenant) {
    return (
      <TenantPortalPage
        token={tenantToken}
        userName={authUser.name}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f7fb" }}>
      {isMobile ? (
        <>
          <AppBar
            position="sticky"
            elevation={0}
            color="transparent"
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(14px)",
            }}
          >
            <Toolbar sx={{ px: 2 }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDrawerToggle}
                sx={{ mr: 1, color: "text.primary" }}
              >
                <MenuIcon />
              </IconButton>
              <Box>
                <Typography variant="overline" color="primary.main">
                  HMO Manager
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {
                    visibleNavItems.find((item) => item.key === activeView)
                      ?.label
                  }
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: drawerWidth,
                borderRight: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </>
      ) : (
        <Box
          component="nav"
          sx={{
            position: "fixed",
            insetY: 0,
            left: 0,
            width: drawerWidth,
            height: "100dvh",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            zIndex: 2,
            overflow: "hidden",
          }}
        >
          {drawerContent}
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          display: { xs: "none", md: "block" },
          p: { xs: 2.25, md: 3 },
          bgcolor: "background.paper",
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="overline"
              color="primary.main"
              sx={{ fontWeight: 700, letterSpacing: "0.24em" }}
            >
              Property management
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {visibleNavItems.find((item) => item.key === activeView)?.label}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box
        component="main"
        sx={{ ml: { md: `${drawerWidth}px` }, p: { xs: 2, md: 3 } }}
      >
        <Suspense
          fallback={
            <Paper variant="outlined" sx={{ borderRadius: 6, p: 2 }}>
              <Typography>Loading...</Typography>
            </Paper>
          }
        >
          <Stack spacing={2.5}>
            {activeView === "dashboard" && <DashboardPage />}
            {activeView === "properties" && <PropertiesPage />}
            {activeView === "tenants" && <TenantsPage token={authUser.token} />}
            {activeView === "tenant-users" && (
              <TenantUsersPage token={authUser.token} />
            )}
            {activeView === "rooms" && <RoomsPage />}
            {activeView === "tenancies" && <TenanciesPage />}
            {activeView === "maintenance" && <MaintenancePage />}
            {activeView === "inspections" && <InspectionsPage />}
            {activeView === "document-reviews" && (
              <DocumentReviewList token={authUser.token} />
            )}
            {activeView === "files" && (
              <FilesPage token={authUser.token} readOnly={isFileViewer} />
            )}
            {activeView === "backup-restore" && <BackupRestorePage />}
            {activeView === "admin-users" && isAdmin && (
              <AdminUsersPage
                token={authUser.token}
                currentUserId={authUser.id}
              />
            )}
          </Stack>
        </Suspense>
      </Box>
    </Box>
  );
}

export default App;
